import { Transition } from '@headlessui/react';
import { Grammerly } from 'iconsax-react';
import { Fragment, forwardRef, useImperativeHandle, useRef } from 'react';
import { ErrorBoundary } from 'react-error-boundary';
import { useOnClickOutside, useToggle } from 'usehooks-ts';
import LoadingLayout from '@/components/layouts/progress-layout/loading-layout';
import EmojiPanel from '@/components/symbol/emoji/emoji-panel';
import StickerPanel from '@/components/symbol/sticker/sticker-panel';
import { SymbolContext } from './symbol-context';
import SymbolSelector from './symbol-selector';

type Props = {
  disabled?: boolean;
  onEmoji: (emoji: string) => void;
  onSticker: (url: string) => void;
};

export type SymbolMenuActions = {
  close: () => void;
};

const SymbolMenu = forwardRef<SymbolMenuActions, Props>(({ disabled, onEmoji, onSticker }, symbolMenuRef) => {
  const ref = useRef<HTMLDivElement>(null);

  const [open, toggleOpen, setOpen] = useToggle(false);

  const close = () => setOpen(false);
  useOnClickOutside(ref, close);
  useImperativeHandle(symbolMenuRef, () => ({ close }));

  return (
    <div
      ref={ref}
      className='h-full self-end ring-0'>
      <button
        type='button'
        disabled={disabled}
        onMouseDown={(e) => {
          e.preventDefault();
          toggleOpen();
        }}
        className='btn btn-square btn-ghost btn-sm size-8'>
        <Grammerly className='size-6 text-body' />
      </button>

      <Transition
        as={Fragment}
        show={open}
        enter='transition ease-out duration-200'
        enterFrom='opacity-0 translate-y-1'
        enterTo='opacity-100 translate-y-0'
        leave='transition ease-in duration-150'
        leaveFrom='opacity-100 translate-y-0'
        leaveTo='opacity-0 translate-y-1'>
        <div className='absolute bottom-full right-1/2 z-20 mb-2 flex h-72 w-screen max-w-80vw desktop:max-w-30vw desktop:right-5 desktop:translate-x-0 translate-x-1/2 flex-col gap-2 rounded-lg bg-neutral-0/90 shadow-lg backdrop-blur'>
          <SymbolContext.Provider
            value={{
              sendEmoji: onEmoji,
              sendSticker: onSticker,
            }}>
            <ErrorBoundary fallback={<LoadingLayout />}>
              <EmojiPanel />
              <StickerPanel />
              <SymbolSelector />
            </ErrorBoundary>
          </SymbolContext.Provider>
        </div>
      </Transition>
    </div>
  );
});

SymbolMenu.displayName = 'SymbolMenu';

export default SymbolMenu;
